import { reactive, ref, onMounted, onUnmounted } from 'vue';
import { databases, appwriteConfig, query, client, functions } from './appwrite';

const DATABASE_ID = appwriteConfig.database;
const COLLECTION_ID = appwriteConfig.collections.syncs;

let instance = null;

export function useSyncs() {
    if (instance) return instance;

    const syncData = reactive({
        datetime: null,
        legCount: 0,
        status: 'running', // default status
    });
    const isInitialized = ref(false);

    const parseSync = (doc) => {
        return {
            datetime: doc.datetime,
            legCount: doc.legCount,
            status: doc.status,
        };
    };

    const fetchLatestSync = async () => {
        try {
            const response = await databases.listDocuments(DATABASE_ID, COLLECTION_ID, [
                query.orderDesc('datetime'),
                query.limit(1)
            ]);

            if (response.documents.length > 0) {
                Object.assign(syncData, parseSync(response.documents[0]));
            }
        } catch (err) {
            console.error('Error fetching latest sync:', err);
        }
    };

    const runSyncLegs = async () => {
        try {
            const response = await functions.createExecution(appwriteConfig.functions.syncLegs, "{}");
            const result = JSON.parse(response.responseBody);

            await fetchLatestSync();
            return { message: result.message, success: true };
        } catch (err) {
            console.error('Sync Error:', err);
            return { message: err.message || 'Failed to sync legs.', success: false };
        }
    };

    const runSyncLeg = async (legId) => {
        try {
            const response = await functions.createExecution(appwriteConfig.functions.syncLegs, `{"legId": "${legId}"}`);
            const result = JSON.parse(response.responseBody);

            return { message: result.message, success: true };
        } catch (err) {
            console.error('Sync Leg Error:', err);
            return { message: err.message || 'Failed to sync leg ' + legId, success: false };
        }
    }

    const initialize = () => {
        if (isInitialized.value) return;

        let unsubscribe = null;
        let reconnectTimeout = null;

        // Create subscription function that can be reused
        const createSubscription = () => {
            // Clean up any existing subscription before creating a new one
            if (unsubscribe) {
                unsubscribe();
            }

            return client.subscribe(`databases.${DATABASE_ID}.collections.${COLLECTION_ID}.documents`,
                (response) => {
                    response.events.forEach(event => {
                        const doc = response.payload;
                        if (event.includes('create') || event.includes('update')) {
                            const incomingSync = parseSync(doc);
                            if (!syncData.datetime || new Date(incomingSync.datetime) > new Date(syncData.datetime)) {
                                Object.assign(syncData, incomingSync);
                            }
                        }
                    });
                },
                // Add error callback
                (error) => {
                    console.error('Subscription error:', error);
                    // Clear any existing timeout
                    if (reconnectTimeout) {
                        clearTimeout(reconnectTimeout);
                    }
                    // Attempt to reconnect after 5 seconds
                    reconnectTimeout = setTimeout(() => {
                        console.log('Attempting to reconnect...');
                        unsubscribe = createSubscription();
                    }, 5000);
                }
            );
        };

        // Initial subscription
        unsubscribe = createSubscription();

        // Cleanup function that removes the subscription and any pending reconnection
        const cleanup = () => {
            if (reconnectTimeout) {
                clearTimeout(reconnectTimeout);
            }
            if (unsubscribe) {
                unsubscribe();
            }
        };

        onMounted(fetchLatestSync);
        onUnmounted(cleanup);

        isInitialized.value = true;
    };

    instance = {
        syncData,
        fetchLatestSync,
        runSyncLegs,
        runSyncLeg,
        initialize
    };

    return instance;
}